import type { DateRange } from '../../components/social-media/types'

/**
 * Is a comment-date filter actually constraining anything?
 *
 * A `DateRange` with neither end is a live object that filters nothing —
 * `buildLabelQuery` would still emit it — so "the user set a date filter" and
 * "a date range object exists" are different questions. One rule, exported,
 * because two places on the page qualify their copy on the answer (the Key
 * Metrics caption and the Cultural Relevance tooltip) and they must not be able
 * to disagree about it.
 */
export function hasCommentDateFilter(range: DateRange | null | undefined): boolean {
  return Boolean(range?.from || range?.to)
}

/**
 * The sentence under "Key Metrics", which is the only place on the page that
 * says WHICH POPULATION each tile was drawn from.
 *
 * 🔴 WHY THIS IS A FUNCTION AND NOT A TEMPLATE LITERAL IN THE PAGE. The strip
 * mixes two populations — Sky Relevant and Sky Attributed are comment
 * percentages, while Meaningful ER and U35 are properties of the POST that
 * arrive from Sprinklr's own insights (`auto_coder_entity_metrics`). Turning on
 * the comment-date filter (#1200) added a control that narrows one of those two
 * and cannot narrow the other, so this caption became the thing standing
 * between a user and a wrong reading. That is worth a name and a test.
 *
 * ⚠ THE COMMENT-DATE FILTER GENUINELY DOES NOT REACH THE POST METRICS, and this
 * is deliberate rather than a gap to close later:
 *
 *   - `source_date` is a column on `auto_coder_pipeline_records`. A post has no
 *     such date, so `/entities/metrics/query` has no `commentDateRange` field
 *     on its request type at all (server/autocoder_entities.go).
 *   - Making the two agree was tried and measured (2026-08-14): narrowing the
 *     post population to posts having a comment that survives the record filter
 *     takes Sports from 27 posts to 0 and Corporate from 55 to 5 — and every one
 *     of those posts carries real reach and engagement. A post's reach does not
 *     become unknown because we hold no February comments on it.
 *
 * So the filter flows PARENT → CHILD only, and the honest move is to say so in
 * the caption rather than to let "Post metrics from 27 posts" sit unqualified
 * next to a date range the reader just typed.
 *
 * @param totalEntities posts matching the ENTITY half of the filter
 * @param commentCount analysed comments matching the whole filter
 * @param commentDateRange the live record-level date filter, or null
 * @param accountFiltered true when the entity filter narrows by `account`.
 *   An account is a property of the POST, so a comment whose parent post was
 *   never matched cannot belong to any account and drops out of the comment
 *   metrics. The caption says so, because the drop is otherwise invisible.
 */
export function keyMetricsCaption(
  totalEntities: number,
  commentCount: number,
  commentDateRange: DateRange | null | undefined,
  accountFiltered = false,
  postsOutsideTable?: number,
): string {
  const accountNote =
    (accountFiltered ? ' · comments not matched to a post have no account, so the account filter leaves them out' : '') +
    (postsOutsideTable
      ? ` · ${postsOutsideTable.toLocaleString()} more post${postsOutsideTable !== 1 ? 's' : ''} outside Sky's Sprinklr table carry comments and are not counted as posts`
      : '')
  const posts = `${totalEntities} post${totalEntities !== 1 ? 's' : ''}`
  const comments = `${commentCount.toLocaleString()} comment${commentCount !== 1 ? 's' : ''}`

  // A range object with neither end constrains nothing, so claiming a narrowing
  // for it would be the same lie in the other direction.
  if (!hasCommentDateFilter(commentDateRange)) {
    return `Post metrics from ${posts} · comment metrics from ${comments}${accountNote}`
  }
  const from = commentDateRange?.from
  const to = commentDateRange?.to

  const dated = from && to ? `dated ${from} to ${to}` : from ? `dated from ${from}` : `dated up to ${to}`

  return (
    `Post metrics from ${posts} · comment metrics from ${comments} ${dated}` +
    ' · the comment date filter narrows comment metrics only, not post metrics' +
    accountNote
  )
}

export default keyMetricsCaption
